/* global BigInt */
import api from '@/api/client'

export async function getStockAssignments(params = {}) {
  const { data } = await api.get('wealth/stock-assignments', { params })
  if (!Array.isArray(data)) throw new Error('Die Positionen konnten nicht gelesen werden.')
  return data
}
// The endpoint excludes synthetic positions using the quote service rules.
export async function getSelectedStockAssignments(ownerIds) {
  return (await Promise.all(ownerIds.map(owner_id => getStockAssignments({ owner_id })))).flat()
}
export function unassignedStockPositions(positions) {
  return positions.filter(position => {
    if (position.wealth_account_id !== null) return false
    const raw = position.stocks_Menge
    if (raw == null || String(raw).trim() === '' || !Number.isFinite(Number(raw))) {
      throw new Error('Ungültige Positionsmenge.')
    }
    return Number(raw) !== 0
  })
}

export async function saveStockAssignments(stockIds, accountId) {
  return (await api.put('wealth/stock-assignments', { stock_ids: stockIds, wealth_account_id: accountId })).data
}

export async function getWealthAccounts(params = {}) {
  const { data } = await api.get('wealth/accounts', { params })
  if (!Array.isArray(data)) throw new Error('Die Kontenliste konnte nicht gelesen werden.')
  return data
}
// The backend accepts one owner per request; combine selected persons here.
export async function getSelectedWealthAccounts(params, ownerIds) {
  if (ownerIds === null) return getWealthAccounts(params)
  const lists = await Promise.all(ownerIds.map(owner_id => getWealthAccounts({ ...params, owner_id })))
  return lists.flat()
}
export async function getSelectedWealthSummary(asOf, ownerIds) {
  if (ownerIds === null) return getWealthSummary(asOf)
  const summaries = await Promise.all(ownerIds.map(id => getWealthSummary(asOf, id)))
  const totals = new Map()
  let withBalance = 0, withoutBalance = 0
  for (const summary of summaries) {
    withBalance += summary.accounts_with_balance
    withoutBalance += summary.accounts_without_balance
    for (const total of summary.totals) {
      const cents = BigInt(total.amount.replace('.', ''))
      totals.set(total.currency, (totals.get(total.currency) || BigInt(0)) + cents)
    }
  }
  return { as_of: asOf, accounts_with_balance: withBalance, accounts_without_balance: withoutBalance,
    totals: [...totals].sort(([a], [b]) => a.localeCompare(b)).map(([currency, cents]) => {
      const digits = (cents < 0 ? -cents : cents).toString().padStart(3, '0')
      return { currency, amount: `${cents < 0 ? '-' : ''}${digits.slice(0, -2)}.${digits.slice(-2)}` }
    }) }
}
export async function getWealthPersons() {
  const { data } = await api.get('wealth/persons')
  if (!Array.isArray(data)) throw new Error('Die Personenliste konnte nicht gelesen werden.')
  return data
}
export async function getWealthSummary(asOf, ownerId) {
  const params = { as_of: asOf }
  if (ownerId != null) params.owner_id = ownerId
  const { data } = await api.get('wealth/summary', { params })
  if (!Array.isArray(data?.totals)) throw new Error('Die Zusammenfassung konnte nicht gelesen werden.')
  return data
}
export async function getWealthAccount(id, asOf) {
  return (await api.get(`wealth/accounts/${id}`, { params: { as_of: asOf } })).data
}
export async function saveWealthAccount({ id, ...payload }) {
  return (id ? await api.patch(`wealth/accounts/${id}`, payload) : await api.post('wealth/accounts', payload)).data
}
export async function getWealthBalances(id, params = {}) {
  const { data } = await api.get(`wealth/accounts/${id}/balances`, { params })
  if (!Array.isArray(data)) throw new Error('Die Kontostände konnten nicht gelesen werden.')
  return [...data].sort((a, b) => a.balance_date.localeCompare(b.balance_date))
}
export async function saveWealthBalance(id, date, payload) {
  return (await api.put(`wealth/accounts/${id}/balances/${date}`, payload)).data
}
export async function deleteWealthBalance(id, date) {
  await api.delete(`wealth/accounts/${id}/balances/${date}`)
}

function valuationRows(data, key) {
  const rows = Array.isArray(data) ? data : data?.[key] ?? data?.items ?? data?.rows
  if (!Array.isArray(rows)) throw new Error('Die Bewertungsdaten konnten nicht gelesen werden.')
  return rows
}
export async function getWealthValuationRules(accountId) {
  return valuationRows((await api.get(`wealth/accounts/${accountId}/valuation-rules`)).data, 'rules')
}
export async function createWealthValuationRule(accountId, payload) {
  return (await api.post(`wealth/accounts/${accountId}/valuation-rules`, payload)).data
}
export async function updateWealthValuationRule(id, payload) {
  return (await api.patch(`wealth/valuation-rules/${id}`, payload)).data
}
export async function rescheduleWealthValuationRule(id, nextDate) {
  return (await api.post(`wealth/valuation-rules/${id}/reschedule`, { next_date: nextDate })).data
}
export async function previewWealthValuationRule(id, through) {
  return valuationRows((await api.post(`wealth/valuation-rules/${id}/preview`, { through })).data, 'valuations')
}
export async function getWealthValuationRuns(id, limit = 50, offset = 0) {
  return valuationRows((await api.get(`wealth/valuation-rules/${id}/runs`, { params: { limit, offset } })).data, 'runs')
}
export async function reverseWealthValuationRun(ruleId, runId, reason) {
  return (await api.post(`wealth/valuation-rules/${ruleId}/runs/${runId}/reverse`, { reason })).data
}
